import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStateValidation } from '../shared/validation/useStateValidation';
import { useBookingDateValidation } from '../shared/validation/useBookingDateValidation';
import { useParkingProductValidation } from '../shared/validation/useParkingProductValidation';
import { usePersonalDetailsValidation } from '../shared/validation/usePersonalDetailsValidation';
import { PaymentContextProvider } from './PaymentContext';
import { useNewBookingStore } from '../../../domain/booking/stores/useNewBookingsStore';
import { getTotalNewBookingPrice } from '../../../domain/booking/models/helpers/getTotalNewBookingPrice';
import { getShouldProceedWithoutPay } from '../../../domain/booking/models/helpers/getShouldProceedWithoutPay';
import { getOriginalGrandTotalPrice } from '../../../domain/booking/models/helpers/getOriginalGrandTotalPrice';
import { PaymentForm } from './PaymentForm';
import { CreditCardType } from '../../../../payment/domain/models';
import { IMicroform } from '../../../../shared/api/cybersource/createFlexMicroform';
import ContentWrap from '../../../../../ui/components/Layout/ContentWrap';
import styled from '@emotion/styled';
import { Css } from '../../../../../ui';
import Responsive from '../../../../../ui/utils/Responsive';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';

interface IPaymentProps {
	nextUrl: string;
	sessionExpiredNextUrl: string;
	changeButtonUrl?: string;
}

export const Payment = observer(
	({ nextUrl, sessionExpiredNextUrl, changeButtonUrl }: IPaymentProps) => {
		useStateValidation([
			useBookingDateValidation(),
			useParkingProductValidation(),
			usePersonalDetailsValidation(),
		]);
		const { t } = useTranslation();

		const [newCard, setNewCard] = useState(true);
		const [newCardType, setNewCardType] = useState<CreditCardType>();
		const [isNewCardNumberValid, setIsNewCardNumberValid] = useState<boolean>();
		const [isNewCardCVVValid, setIsNewCardCVVValid] = useState<boolean>();
		const [microformInstance, setMicroformInstance] = useState<IMicroform>();

		const { originalBooking, booking } = useNewBookingStore();

		const originalTotalPrice =
			originalBooking && getOriginalGrandTotalPrice(originalBooking);
		const totalPrice = getTotalNewBookingPrice(booking);
		const shouldProceedWithoutPay = getShouldProceedWithoutPay(
			originalTotalPrice,
			totalPrice,
		);

		return (
			<PaymentContextProvider
				value={{
					newCard,
					setNewCard,
					newCardType,
					setNewCardType,
					isNewCardNumberValid,
					setIsNewCardNumberValid,
					isNewCardCVVValid,
					setIsNewCardCVVValid,
					setMicroformInstance,
					microformInstance,
				}}
			>
				{shouldProceedWithoutPay && (
					<ContentWrap>
						<NoPaymentRequiredText>
							{t('web_booking_amend_no_payment_due')}
						</NoPaymentRequiredText>
					</ContentWrap>
				)}
				<PaymentForm
					nextUrl={nextUrl}
					sessionExpiredNextUrl={sessionExpiredNextUrl}
					changeButtonUrl={changeButtonUrl}
				/>
			</PaymentContextProvider>
		);
	},
);

const NoPaymentRequiredText = styled.div`
	display: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: block;
		font-size: ${Css.pxToRem(21)};
		font-weight: 600;
		margin-top: 30px;
	}
`;
